<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="tunerStore.isOpen && tunerStore.isFullscreen"
        class="fixed inset-0 z-[1001] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
        @click.self="tunerStore.minimize()"
      >
        <div
          class="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl px-5 py-5 md:px-7 md:py-6 max-h-[92vh] overflow-y-auto select-none text-gray-900 dark:text-white"
        >
          <!-- Header -->
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <TuningForkIcon class="w-6 h-6 text-gray-800 dark:text-white" />
              <h2 class="text-xl font-bold tracking-tight">{{ $t('tuner.title') }}</h2>
            </div>
            <button
              type="button"
              @click="closeTuner"
              class="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              :title="$t('tuner.close')"
            >
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Instrument selector -->
          <TriadSelect
            :modelValue="tunerStore.selectedTuning.id"
            :options="tuningOptions"
            :label="$t('tuner.instrumentSelector')"
            @change="selectTuning"
            root-class="block w-full mt-4"
          />

          <!-- Reference sound (só com instrumento: sem cordas, sem referência) -->
          <TriadSelect
            v-if="(tunerStore.selectedTuning.strings || []).length > 0"
            :modelValue="tunerStore.referenceInstrument"
            :options="referenceOptions"
            :label="$t('tuner.referenceSound')"
            @change="selectReferenceSound"
            root-class="block w-full mt-3"
          />

          <!-- Mostrador -->
          <div class="flex justify-center mt-5">
            <TriadTunerDial :size="268" />
          </div>

          <!-- Barra linear fina (mobile, como no mockup) -->
          <div class="md:hidden mt-1 px-1">
            <div class="flex justify-between text-[10px] font-mono text-gray-400 mb-1">
              <span>-50</span>
              <span>+50</span>
            </div>
            <div class="relative h-2 bg-gray-100 dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-700">
              <div class="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-1 bg-emerald-500/60 rounded-full"></div>
              <div
                v-if="tunerState.note"
                class="absolute top-1/2 w-3 h-3 rounded-full shadow"
                :class="tunerState.inTune ? 'bg-emerald-500' : 'bg-rose-500'"
                :style="{ left: `calc(50% + ${linearOffset}% )`, transform: 'translate(-50%, -50%)' }"
              ></div>
            </div>
          </div>

          <!-- Cordas (oculta no modo cromático livre) -->
          <div
            v-if="(tunerStore.selectedTuning.strings || []).length > 0"
            class="mt-5 grid grid-cols-3 sm:grid-cols-6 gap-2"
          >
            <button
              v-for="(str, idx) in tunerStore.selectedTuning.strings"
              :key="idx"
              type="button"
              @click="playReferenceTone(str.frequency)"
              class="px-1 py-2 rounded-xl border flex flex-col items-center transition-all"
              :class="[
                isClosestString(str)
                  ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-400/40'
                  : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 text-gray-600 dark:text-gray-300'
              ]"
              :title="$t('tuner.referenceTone', { note: `${str.note}${str.octave}` })"
            >
              <span class="text-[10px] text-gray-400 dark:text-gray-500 font-medium">{{ str.label }}</span>
              <span class="text-base font-bold leading-tight">{{ str.note }}<sub class="text-[10px]">{{ str.octave }}</sub></span>
              <span class="text-[10px] text-gray-400 dark:text-gray-500 font-mono">{{ str.frequency.toFixed(0) }}Hz</span>
            </button>
          </div>

          <!-- Aviso de erro do microfone -->
          <div
            v-if="!tunerState.isListening && (tunerState.errorKey || tunerState.error)"
            class="mt-4 p-3 rounded-xl border border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 text-left"
            role="alert"
          >
            <p class="text-sm font-semibold text-rose-700 dark:text-rose-300">
              {{ $t(`tuner.${tunerState.errorKey || 'micGeneric'}`) }}
            </p>
            <button
              type="button"
              @click="toggleMic"
              class="mt-2 text-xs font-bold text-rose-700 dark:text-rose-300 underline underline-offset-2 hover:opacity-80"
            >
              {{ $t('tuner.micRetry') }}
            </button>
          </div>

          <!-- Rodapé: mic + calibração -->
          <div class="flex items-center justify-between mt-5 gap-2">
            <button
              type="button"
              @click="toggleMic"
              class="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
              :style="{ backgroundColor: tunerState.isListening ? '#e5487a' : '#22c55e' }"
              :title="tunerState.isListening ? $t('tuner.micStop') : $t('tuner.micStart')"
            >
              {{ tunerState.isListening ? $t('tuner.micStop') : $t('tuner.micStart') }}
            </button>
            <div class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <span>A4</span>
              <button
                type="button"
                @click="tunerState.a4Calibration = Math.max(430, tunerState.a4Calibration - 1)"
                class="px-2 py-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-800 dark:text-white border border-gray-200 dark:border-gray-700"
              >
                -
              </button>
              <span class="font-mono font-bold text-gray-800 dark:text-white">{{ tunerState.a4Calibration }}Hz</span>
              <button
                type="button"
                @click="tunerState.a4Calibration = Math.min(450, tunerState.a4Calibration + 1)"
                class="px-2 py-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-800 dark:text-white border border-gray-200 dark:border-gray-700"
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { tunerService } from '@/service/audio/tuner.service';
import { TUNING_PRESETS } from '@/service/audio/audioEngine';
import tunerStore, { REFERENCE_INSTRUMENTS, CHROMATIC_PRESET } from '@/store/tuner.store';
import { playReferenceFrequency, preloadReferenceNotes, warmUpPlayback } from '@/utils/soundfontPlayer';
import TuningForkIcon from '@/components/icons/TuningForkIcon.vue';
import TriadTunerDial from '@/components/audio/TriadTunerDial.vue';
import TriadSelect from '@/components/common/TriadSelect.vue';

const tunerState = tunerService.state;
const { t } = useI18n();

const tuningOptions = computed(() => [
  { value: CHROMATIC_PRESET.id, label: t('tuner.noInstrument') },
  ...TUNING_PRESETS.map((t) => ({ value: t.id, label: `${t.instrumentLabel} - ${t.name}` })),
]);

const selectTuning = (id) => {
  if (id === CHROMATIC_PRESET.id) {
    tunerStore.selectedTuning = CHROMATIC_PRESET;
    return;
  }
  const found = TUNING_PRESETS.find((t) => t.id === id);
  if (found) tunerStore.selectedTuning = found;
};

const referenceOptions = computed(() =>
  REFERENCE_INSTRUMENTS.map((r) => ({ value: r.value, label: t(r.labelKey) }))
);

const selectReferenceSound = (id) => {
  tunerStore.setReferenceInstrument(id);
};

// Pré-carrega os samples das cordas (L2 persistente: reload não refaz download)
// e aquece o AudioContext para o primeiro toque não pagar criação/resume.
const preloadStrings = () => {
  warmUpPlayback();
  const freqs = (tunerStore.selectedTuning.strings || []).map((s) => s.frequency);
  preloadReferenceNotes(freqs, tunerStore.referenceInstrument);
};

watch(
  [() => tunerStore.isOpen, () => tunerStore.selectedTuning, () => tunerStore.referenceInstrument],
  ([isOpen]) => {
    if (isOpen) preloadStrings();
  },
  { immediate: true }
);

const linearOffset = computed(() => {
  const cents = Math.max(-50, Math.min(50, tunerState.smoothedCents || 0));
  return (cents / 50) * 46;
});

const isClosestString = (str) => {
  if (!tunerState.note) return false;
  return str.note === tunerState.note.name && str.octave === tunerState.note.octave;
};

const playReferenceTone = (freq) => {
  // Sample real do instrumento selecionado (com fallback sintetizado offline)
  playReferenceFrequency(freq, tunerStore.referenceInstrument, 2.5, 0.8);
};

const toggleMic = async () => {
  if (tunerState.isListening) {
    tunerService.stop();
  } else {
    await tunerService.start();
  }
};

const closeTuner = () => {
  tunerService.stop();
  tunerStore.close();
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
